// SettingsTab.js — onglet Settings de Timbo, refondu en 5 blocs empilés
// pleine largeur (comme Performance), avec un vrai monitoring des services.
//
// Leçon de l'incident du 21 juil.–5 août : rien ne criait. Ici on rend les
// pannes visibles (Échéances, Connections, Notifications) et un service sans
// credentials s'affiche GRIS (unknown), jamais vert. Design system §9/§12/§18.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from './i18n';
import { signOut, currentUser } from './auth';
import { getCategories, saveCategories, sanitizeCategory } from './categories';
import { getPersonas, savePersonas, sanitizePersona } from './personas';
import { getDurations, saveDurations, sanitizeDuration } from './durations';
import { getCreators } from './creators';
import PerfBlockHeader from './perf/PerfBlockHeader';
import './shared/layout.css';
import './SettingsTab.css';
import { DEFAULT_CREATOR_TYPE } from './creatorType';
import { NAME_FORMAT_V2 } from './namingConfig';

const BACKEND_FALLBACK = 'https://timbo-production.up.railway.app';

/* ── petits utilitaires ──────────────────────────────────────────── */

const ST_META = {
  ok:      { cls: 'sb-ok',      label: 'OK' },
  warn:    { cls: 'sb-warn',    label: 'WARN' },
  error:   { cls: 'sb-error',   label: 'DOWN' },
  unknown: { cls: 'sb-unknown', label: 'NON CONFIG.' },
};

function Sb({ status }) {
  const m = ST_META[status] || ST_META.unknown;
  return <span className={`sb ${m.cls}`}><span className="sb-dot" />{m.label}</span>;
}

function fmtDate(iso, fmt) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  if (fmt === 'MDY') return `${mm}/${dd}/${yyyy}`;
  if (fmt === 'ISO') return `${yyyy}-${mm}-${dd}`;
  return `${dd}/${mm}/${yyyy}`; // DMY par défaut
}

function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

/* ── bloc repliable (mirroir du chrome .perf-block, tokens corrigés) ── */

function Block({ id, glyph, title, sub, right, collapsed, onToggle, children }) {
  return (
    <section className={`tb-card sb-block${collapsed ? ' collapsed' : ''}`}>
      <PerfBlockHeader
        icon={glyph}
        title={title}
        subtitle={sub}
        actions={right}
        collapsible
        collapsed={collapsed}
        onToggle={() => onToggle(id)}
      />
      {!collapsed && <div className="sb-block-body">{children}</div>}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════ */

export default function SettingsTab({
  apiBase,
  appTheme, setAppTheme,
  appLang, setAppLang,
  apiKeyDraft, setApiKeyDraft,
  anthropicDraft, setAnthropicDraft,
  savedFeedback, saveApiKeys,
  wordingPhrases, saveWordingPhrases,
  brandName, setBrandName,
  brandSubtitle, setBrandSubtitle,
  onNavigate,
  focusService,
}) {
  const t = useTranslation(appLang);
  const BASE = apiBase || BACKEND_FALLBACK;

  /* — repli des blocs — */
  const [collapsed, setCollapsed] = useState({});
  const toggle = useCallback((id) => setCollapsed(c => ({ ...c, [id]: !c[id] })), []);

  /* — santé des services — */
  const [health, setHealth] = useState({ loading: true, data: null, error: null, at: null });
  const fetchHealth = useCallback((fresh) => {
    setHealth(h => ({ ...h, loading: true }));
    fetch(`${BASE}/api/health/services${fresh ? '?fresh=1' : ''}`)
      .then(r => r.json())
      .then(data => setHealth({ loading: false, data, error: null, at: Date.now() }))
      .catch(e => setHealth({ loading: false, data: null, error: String(e.message || e), at: Date.now() }));
  }, [BASE]);
  useEffect(() => { fetchHealth(false); }, [fetchHealth]);

  /* — opened from the global banner: expand Connections, scroll to the card, highlight it — */
  const [focusKey, setFocusKey] = useState(null);
  useEffect(() => {
    if (!focusService || !focusService.key) return undefined;
    setCollapsed(c => ({ ...c, connections: false }));
    setFocusKey(focusService.key);
    const scroll = setTimeout(() => {
      const el = document.getElementById(`sb-card-${focusService.key}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const clear = setTimeout(() => setFocusKey(null), 4000);
    return () => { clearTimeout(scroll); clearTimeout(clear); };
  }, [focusService, health.data]);

  const services = (health.data && health.data.services) || [];
  const counts = (health.data && health.data.counts) || { ok: 0, warn: 0, error: 0, unknown: 0 };
  const deadlines = (health.data && health.data.deadlines) || [];

  /* — filtre Connections (dim, jamais de reflow) — */
  const [connFilter, setConnFilter] = useState('all');
  const nIssues = counts.warn + counts.error;
  const nUnconf = counts.unknown;
  // Optional, unconfigured services (e.g. Vercel token) are not problems: they don't count against "x/y OK".
  const isOptionalOff = (s) => s.optional && s.status === 'unknown';
  const nRequired = services.filter(s => !isOptionalOff(s)).length;
  function cardMod(s) {
    if (connFilter === 'issues') return (s.status === 'warn' || s.status === 'error') ? 'hl' : 'dim';
    if (connFilter === 'unconfigured') return s.status === 'unknown' ? 'hl' : 'dim';
    return '';
  }

  /* — clés navigateur (ex-panneau API Keys, absorbé dans Connections) — */
  const [keyOpen, setKeyOpen] = useState({});

  /* — fenêtre des échéances — */
  const [deadWindow, setDeadWindow] = useState(90);
  const shownDeadlines = useMemo(
    () => deadlines.filter(d => d.overdue || (d.daysLeft != null && d.daysLeft <= deadWindow)),
    [deadlines, deadWindow]
  );
  const nOverdue = deadlines.filter(d => d.overdue).length;

  /* — préférences — */
  const [dateFmt, setDateFmt] = useState(() => localStorage.getItem('timbo_dateformat_v1') || 'DMY');
  const pickDateFmt = (f) => { setDateFmt(f); try { localStorage.setItem('timbo_dateformat_v1', f); } catch {} };

  /* — listes éditables (nomenclature) — */
  const [cats, setCats] = useState(getCategories);
  const [personas, setPersonas] = useState(getPersonas);
  const [durs, setDurs] = useState(getDurations);
  const creators = useMemo(() => getCreators(), []);
  const commitCats = (l) => { setCats(l); saveCategories(l); };
  const commitPersonas = (l) => { setPersonas(l); savePersonas(l); };
  const commitDurs = (l) => { setDurs(l); saveDurations(l); };

  /* — aperçu live du nom de fichier — */
  const [pv, setPv] = useState({
    creator: (getCreators()[0] || 'Creator'),
    dur: (getDurations()[0] || '30'),
    persona: (getPersonas()[0] || 'broad'),
    cat: (getCategories()[0] || 'fashion'),
    feature: 'feature',
    lang: 'en',
  });
  const previewName = NAME_FORMAT_V2
    ? `${DEFAULT_CREATOR_TYPE}-${pv.creator}_CreativeName-1_HOOK-ugc_DUR-${pv.dur}_P-${pv.persona}_PO_[${pv.feature}]_[${pv.lang}].mp4`
    : `${DEFAULT_CREATOR_TYPE}-${pv.creator}_CreativeName-1_HOOK-ugc_DUR-${pv.dur}_PERSONA-${pv.persona}` +
      `_CAT-${pv.cat}_PO_[${pv.feature}]_[${pv.lang}].mp4`;

  /* — notifications (config locale) — */
  const [channels, setChannels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('timbo_notif_channels_v1')) || { banner: true, slack: true, email: false }; }
    catch { return { banner: true, slack: true, email: false }; }
  });
  const toggleChannel = (k) => {
    if (k === 'email') return; // inactif (à câbler plus tard)
    const next = { ...channels, [k]: !channels[k] };
    setChannels(next);
    try { localStorage.setItem('timbo_notif_channels_v1', JSON.stringify(next)); } catch {}
  };
  const DEFAULT_RULES = [
    { id: 'expiry',    labelKey: 'settings.rule_expiry',   def: t('settings.def_expiry'),    level: 'warn' },
    { id: 'quota',     labelKey: 'settings.rule_quota',    def: t('settings.def_quota'),     level: 'warn' },
    { id: 'deploy',    labelKey: 'settings.rule_deploy',   def: t('settings.def_deploy'),    level: 'error' },
    { id: 'sha',       labelKey: 'settings.rule_sha',      def: t('settings.def_sha'),       level: 'error' },
    { id: 'failopen',  labelKey: 'settings.rule_failopen', def: t('settings.def_immediate'), level: 'error' },
    { id: 'payment',   labelKey: 'settings.rule_payment',  def: t('settings.def_immediate'), level: 'error' },
    { id: 'nosync',    labelKey: 'settings.rule_nosync',   def: t('settings.def_nosync'),    level: 'warn' },
  ];
  const [rules, setRules] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('timbo_notif_rules_v1')) || {};
      return DEFAULT_RULES.map(r => ({ ...r, seuil: saved[r.id] != null ? saved[r.id] : r.def }));
    } catch { return DEFAULT_RULES.map(r => ({ ...r, seuil: r.def })); }
  });
  const editRule = (id, v) => {
    const next = rules.map(r => r.id === id ? { ...r, seuil: v } : r);
    setRules(next);
    try { localStorage.setItem('timbo_notif_rules_v1', JSON.stringify(Object.fromEntries(next.map(r => [r.id, r.seuil])))); } catch {}
  };

  /* — branding : logo — */
  const [logoData, setLogoData] = useState(() => localStorage.getItem('timbo_logo_v1') || '');
  const logoInput = useRef(null);
  const onLogoPick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { const d = String(reader.result); setLogoData(d); try { localStorage.setItem('timbo_logo_v1', d); } catch {} };
    reader.readAsDataURL(f);
  };

  /* — heartbeat Supabase (supprime l'échéance de pause) — */
  const [beat, setBeat] = useState(null); // null | 'run' | 'ok' | 'err'
  const runHeartbeat = () => {
    setBeat('run');
    fetch(`${BASE}/api/health/supabase-heartbeat`, { method: 'POST' })
      .then(r => r.json())
      .then(j => { setBeat(j && j.ok ? 'ok' : 'err'); if (j && j.ok) fetchHealth(true); })
      .catch(() => setBeat('err'));
  };

  const u = currentUser();
  const railway = services.find(s => s.key === 'railway');
  const shaFact = railway && (railway.facts || []).find(f => /SHA en prod/i.test(f.k));
  const shaMainFact = railway && (railway.facts || []).find(f => /= main/i.test(f.k));
  const frontVersion = process.env.REACT_APP_VERSION || null;

  /* ═══ rendu ═══════════════════════════════════════════════════════ */
  return (
    <div className="tb-page sb-page">

      {/* ─────────────── BLOC 1 · Échéances ─────────────── */}
      <Block
        id="deadlines" glyph="◷" title={t('settings.deadlines')}
        collapsed={collapsed.deadlines} onToggle={toggle}
        right={
          <>
            <span className="sb-hcount">
              {nOverdue > 0 ? `${nOverdue} ${t('settings.overdue')} · ` : ''}
              {shownDeadlines.length} {t('settings.under')} {deadWindow} {t('settings.days_short')}
            </span>
            <div className="sb-seg sb-seg-sm">
              {[30, 90, 180].map(w => (
                <button key={w} className={deadWindow === w ? 'on' : ''} onClick={() => setDeadWindow(w)}>{w} {t('settings.days_short')}</button>
              ))}
            </div>
          </>
        }
      >
        {health.loading && !health.data && <div className="sb-muted">{t('settings.loading')}</div>}
        {health.error && <div className="sb-inline-err">{t('settings.backend_unreachable')} — {health.error}</div>}
        {health.data && shownDeadlines.length === 0 && (
          <div className="sb-muted">{t('settings.nothing_expires_pre')} {deadWindow} {t('settings.nothing_expires_post')}</div>
        )}
        {shownDeadlines.map(d => {
          const tone = (d.overdue || (d.daysLeft != null && d.daysLeft < 7)) ? 'error'
            : (d.daysLeft != null && d.daysLeft < 30) ? 'warn' : 'gray';
          const pct = d.overdue ? 100 : clamp(Math.round((1 - (d.daysLeft || 0) / deadWindow) * 100), 2, 100);
          return (
            <div key={d.id} className="sb-dl">
              <div className="sb-dl-main">
                <div className="sb-dl-label">{d.label}</div>
                {d.desc && <div className="sb-dl-desc">{d.desc}</div>}
              </div>
              <div className="sb-dl-right">
                <div className={`sb-dl-count tone-${tone}`}>
                  {d.overdue ? t('settings.overdue') : `${d.daysLeft} ${t('settings.days_short')}`}
                </div>
                <div className="sb-dl-date">{fmtDate(d.at, dateFmt)}</div>
              </div>
              <div className="sb-dl-bar"><span className={`sb-dl-fill tone-${tone}`} style={{ width: pct + '%' }} /></div>
              {d.removable && d.id && d.id.indexOf('sb-') === 0 && (
                <button className="sb-dl-fix" onClick={runHeartbeat} disabled={beat === 'run'}>
                  {beat === 'run' ? t('settings.heartbeat_running') : beat === 'ok' ? t('settings.heartbeat_ok') : t('settings.heartbeat_do')}
                </button>
              )}
              {d.removable && d.id === 'meta-token' && (
                <a className="sb-dl-fix" href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer">
                  {t('settings.meta_token_link')}
                </a>
              )}
            </div>
          );
        })}
      </Block>

      {/* ─────────────── BLOC 2 · Connections ─────────────── */}
      <Block
        id="connections" glyph="⚯" title={t('settings.connections')}
        sub={health.data ? `${counts.ok}/${nRequired} OK${counts.optional ? ` · ${counts.optional} optional` : ''}` : null}
        collapsed={collapsed.connections} onToggle={toggle}
        right={
          <>
            <div className="sb-chips">
              <button className={connFilter === 'all' ? 'chip active' : 'chip'} onClick={() => setConnFilter('all')}>{t('settings.conn_all')}<span className="count">{services.length}</span></button>
              <button className={connFilter === 'issues' ? 'chip active' : 'chip'} onClick={() => setConnFilter('issues')}>{t('settings.conn_issues')}<span className="count">{nIssues}</span></button>
              <button className={connFilter === 'unconfigured' ? 'chip active' : 'chip'} onClick={() => setConnFilter('unconfigured')}>{t('settings.conn_unconfigured')}<span className="count">{nUnconf}</span></button>
            </div>
            <button className="sb-refresh" onClick={() => fetchHealth(true)} disabled={health.loading} title={t('settings.refresh_title')}>
              {health.loading ? '…' : '↻'}
            </button>
          </>
        }
      >
        {health.error && <div className="sb-inline-err">{t('settings.cannot_reach')} {BASE} — {health.error}</div>}
        {health.loading && !health.data && <div className="sb-muted">{t('settings.querying_services')}</div>}
        <div className="sb-grid">
          {services.map(s => {
            const mod = cardMod(s);
            const canKey = s.key === 'elevenlabs' || s.key === 'anthropic';
            return (
              <div key={s.key} id={`sb-card-${s.key}`} className={`sb-card ${mod}${focusKey === s.key ? ' focus' : ''}${isOptionalOff(s) ? ' optional' : ''}`}>
                <div className="sb-card-top">
                  <span className={`sb-logo logo-${s.key}`}>{s.label.slice(0, 1)}</span>
                  <div className="sb-card-id">
                    <div className="sb-card-name">{s.label}</div>
                    <div className="sb-card-role">{s.role}</div>
                  </div>
                  {isOptionalOff(s) ? <span className="sb sb-unknown"><span className="sb-dot" />OPTIONAL</span> : <Sb status={s.status} />}
                </div>
                {s.check && <div className="sb-card-check">{s.check}</div>}
                {(s.facts || []).map((f, i) => (
                  <div key={i} className="sb-fact">
                    <span className="sb-fact-k">{f.k}</span>
                    <span className={`sb-fact-v tone-${f.tone || 'none'}`}>{f.v}</span>
                  </div>
                ))}
                {s.quota && s.quota.pct != null && (
                  <div className="sb-quota">
                    <div className="sb-quota-head">
                      <span>{s.quota.label}</span>
                      <span>{Number(s.quota.used).toLocaleString('en-US')}{s.quota.unit === '%' ? '' : ` / ${Number(s.quota.limit).toLocaleString('en-US')}`} {s.quota.unit}</span>
                    </div>
                    <div className="sb-quota-bar"><span className={s.quota.pct >= 80 ? 'over' : ''} style={{ width: clamp(s.quota.pct, 0, 100) + '%' }} /></div>
                  </div>
                )}
                {(s.notes || []).length > 0 && (
                  <div className="sb-notes">{s.notes.map((n, i) => <div key={i}>{n}</div>)}</div>
                )}
                {s.optionalHint && <div className="sb-optional-hint">{s.optionalHint}</div>}
                {canKey && (
                  <div className="sb-card-act">
                    <button className="sb-linkbtn" onClick={() => setKeyOpen(o => ({ ...o, [s.key]: !o[s.key] }))}>
                      {keyOpen[s.key] ? t('settings.hide_key') : t('settings.configure_key')}
                    </button>
                    {keyOpen[s.key] && (
                      <div className="sb-keyrow">
                        <input
                          type="password"
                          className="sb-input"
                          placeholder={s.key === 'elevenlabs' ? 'sk_…' : 'sk-ant-…'}
                          value={s.key === 'elevenlabs' ? apiKeyDraft : anthropicDraft}
                          onChange={e => (s.key === 'elevenlabs' ? setApiKeyDraft : setAnthropicDraft)(e.target.value)}
                        />
                        <button className="sb-btn" onClick={saveApiKeys}>{t('save')}</button>
                        {savedFeedback && <span className="sb-saved">✓</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {health.data && (
          <div className="sb-checked">
            {t('settings.checked')} {fmtDate(health.data.checkedAt, dateFmt)} · {health.data.cached ? t('settings.cache') : t('settings.realtime')}
            {' · '}{t('settings.keys_note')}
          </div>
        )}
      </Block>

      {/* ─────────────── BLOC 3 · Notifications ─────────────── */}
      <Block id="notifications" glyph="◉" title={t('settings.notifications')} collapsed={collapsed.notifications} onToggle={toggle}>
        <div className="sb-chan-row">
          {[
            { k: 'banner', label: t('settings.notif_banner'), desc: t('settings.notif_banner_desc'), active: true },
            { k: 'slack', label: t('settings.notif_slack'), desc: t('settings.notif_slack_desc'), active: true },
            { k: 'email', label: t('settings.notif_email'), desc: t('settings.notif_email_desc'), active: false },
          ].map(c => (
            <div key={c.k} className={`sb-chan${c.active ? '' : ' off'}`}>
              <div className="sb-chan-txt">
                <div className="sb-chan-label">{c.label}</div>
                <div className="sb-chan-desc">{c.desc}</div>
              </div>
              <button
                className={`sb-toggle${channels[c.k] ? ' on' : ''}${c.active ? '' : ' disabled'}`}
                onClick={() => toggleChannel(c.k)}
                disabled={!c.active}
                aria-label={c.label}
              ><span /></button>
            </div>
          ))}
        </div>

        <table className="sb-rules">
          <thead><tr><th>{t('settings.col_rule')}</th><th>{t('settings.col_threshold')}</th><th>{t('settings.col_level')}</th></tr></thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id}>
                <td>{t(r.labelKey)}</td>
                <td><input className="sb-rule-in" value={r.seuil} onChange={e => editRule(r.id, e.target.value)} /></td>
                <td><span className={`sb ${r.level === 'error' ? 'sb-error' : 'sb-warn'}`}><span className="sb-dot" />{r.level === 'error' ? t('settings.level_broken') : t('settings.level_warn')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="sb-legend">
          <span className="sb sb-ok"><span className="sb-dot" />OK</span>
          <span className="sb sb-warn"><span className="sb-dot" />{t('settings.level_warn')}</span>
          <span className="sb sb-error"><span className="sb-dot" />{t('settings.level_broken')}</span>
          <span className="sb sb-unknown"><span className="sb-dot" />{t('settings.legend_unconfigured')}</span>
        </div>
      </Block>

      {/* ─────────────── BLOC 4 · Account ─────────────── */}
      <Block id="account" glyph="◍" title={t('settings.account')} collapsed={collapsed.account} onToggle={toggle}>
        {/* Session */}
        <div className="sb-sub">{t('settings.session')}</div>
        <div className="sb-mini-grid">
          <div className="sb-mini">
            <div className="sb-mini-k">{t('settings.acct_account')}</div>
            <div className="sb-mini-v">{u.email || t('settings.not_connected')}</div>
            <div className="sb-mini-x">{u.role === 'owner' ? t('settings.owner') : t('settings.guest')}</div>
          </div>
          <div className="sb-mini">
            <div className="sb-mini-k">{t('settings.workspace')}</div>
            <div className="sb-mini-v">{t('settings.shared')}</div>
            <div className="sb-mini-x">timbo</div>
          </div>
          <div className="sb-mini">
            <div className="sb-mini-k">{t('settings.front_vercel')}</div>
            <div className="sb-mini-v">{frontVersion || '—'}</div>
            <div className="sb-mini-x">{frontVersion ? t('settings.build') : t('settings.not_exposed')}</div>
          </div>
          <div className="sb-mini">
            <div className="sb-mini-k">{t('settings.backend_railway')}</div>
            <div className="sb-mini-v">{shaFact ? shaFact.v : '—'}</div>
            <div className={`sb-mini-x ${shaMainFact ? `tone-${shaMainFact.tone || 'none'}` : ''}`}>
              {shaMainFact ? shaMainFact.v : (shaFact ? t('settings.github_token_absent') : t('settings.not_exposed'))}
            </div>
          </div>
        </div>

        {/* Préférences */}
        <div className="sb-sub">{t('settings.preferences')}</div>
        <div className="sb-pref">
          <label>{t('settings.theme')}</label>
          <div className="sb-seg">
            {[['light', t('settings.theme_light')], ['dark', t('settings.theme_dark')], ['auto', t('settings.theme_auto')]].map(([k, l]) => (
              <button key={k} className={appTheme === k ? 'on' : ''} onClick={() => setAppTheme && setAppTheme(k)}>{l}</button>
            ))}
          </div>
        </div>
        <div className="sb-pref">
          <label>{t('settings.date_format')}</label>
          <div className="sb-seg">
            {[['DMY', '31/12'], ['MDY', '12/31'], ['ISO', '2026-…']].map(([k, l]) => (
              <button key={k} className={dateFmt === k ? 'on' : ''} onClick={() => pickDateFmt(k)}>{l}</button>
            ))}
          </div>
        </div>

        {/* Phrases d'attente (ex-Wording, orienté écran) */}
        <div className="sb-sub">{t('settings.waiting_phrases')}</div>
        <div className="sb-hint">{t('settings.waiting_phrases_hint')}</div>
        <div className="sb-phrases">
          {wordingPhrases.map((p, i) => (
            <div key={i} className="sb-phrase">
              <input className="sb-input" value={p} onChange={e => { const n = [...wordingPhrases]; n[i] = e.target.value; saveWordingPhrases(n); }} />
              <button className="sb-x" onClick={() => saveWordingPhrases(wordingPhrases.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <button className="sb-add" onClick={() => saveWordingPhrases([...wordingPhrases, 'New phrase…'])}>{t('settings.add')}</button>
        </div>

        {/* Branding */}
        <div className="sb-sub">{t('settings.branding')}</div>
        <div className="sb-brand">
          <div className="sb-brand-fields">
            <label>{t('settings.brand_name')}
              <input className="sb-input" value={brandName || ''} onChange={e => setBrandName && setBrandName(e.target.value)} placeholder="TIMBO" />
            </label>
            <label>{t('settings.brand_subtitle')}
              <input className="sb-input" value={brandSubtitle || ''} onChange={e => setBrandSubtitle && setBrandSubtitle(e.target.value)} placeholder="VIDEO REMIX STUDIO" />
            </label>
          </div>
          <div className="sb-brand-logo">
            <div className="sb-logo-preview">{logoData ? <img src={logoData} alt="logo" /> : <span>{t('settings.no_logo')}</span>}</div>
            <button className="sb-btn-outline" onClick={() => logoInput.current && logoInput.current.click()}>{t('settings.replace')}</button>
            <input ref={logoInput} type="file" accept="image/*" hidden onChange={onLogoPick} />
          </div>
        </div>

        <div className="sb-account-actions">
          <button className="sb-btn-outline" onClick={() => window.dispatchEvent(new Event('timbo-open-onboarding'))}>{t('settings.replay_tutorial')}</button>
          <button className="sb-btn-danger" onClick={() => { signOut().finally(() => window.location.reload()); }}>{t('settings.logout')}</button>
        </div>
      </Block>

      {/* ─────────────── BLOC 5 · Naming convention ─────────────── */}
      <Block id="naming" glyph="#" title={t('settings.naming')} collapsed={collapsed.naming} onToggle={toggle}>
        {/* Modèle */}
        <div className="sb-sub">{t('settings.model')}</div>
        <div className="sb-model">
          {NAME_FORMAT_V2
            ? <>{DEFAULT_CREATOR_TYPE}-<em>[Creator]</em>_<em>[CreativeName]</em>-N_HOOK-<em>[ugc|aigc]</em>_DUR-<em>[{durs.join('|')}]</em>_P-<em>[{personas.join('|')}]</em>_<em>[PO|STATIC-dim]</em>_[<em>[feature]</em>]_[<em>[lang]</em>].mp4</>
            : <>{DEFAULT_CREATOR_TYPE}-<em>[Creator]</em>_<em>[CreativeName]</em>-N_HOOK-ugc_DUR-<em>[{durs.join('|')}]</em>_PERSONA-<em>[{personas.join('|')}]</em>_CAT-<em>[{cats.join('|')}]</em>_PO_[<em>[feature]</em>]_[<em>[lang]</em>].mp4</>}
        </div>

        {/* Aperçu live */}
        <div className="sb-sub">{t('settings.live_preview')}</div>
        <div className="sb-pv-controls">
          <label>{t('settings.creator')}<select value={pv.creator} onChange={e => setPv({ ...pv, creator: e.target.value })}>{creators.map(c => <option key={c} value={c}>{c}</option>)}</select></label>
          <label>{t('settings.duration')}<select value={pv.dur} onChange={e => setPv({ ...pv, dur: e.target.value })}>{durs.map(d => <option key={d} value={d}>{d}</option>)}</select></label>
          <label>{t('settings.persona')}<select value={pv.persona} onChange={e => setPv({ ...pv, persona: e.target.value })}>{personas.map(p => <option key={p} value={p}>{p}</option>)}</select></label>
          <label>{t('settings.category')}<select value={pv.cat} onChange={e => setPv({ ...pv, cat: e.target.value })}>{cats.map(c => <option key={c} value={c}>{c}</option>)}</select></label>
          <label>{t('settings.feature')}<input value={pv.feature} onChange={e => setPv({ ...pv, feature: e.target.value })} /></label>
          <label>{t('settings.language')}<select value={pv.lang} onChange={e => setPv({ ...pv, lang: e.target.value })}>{['en', 'fr', 'es', 'de', 'pt'].map(l => <option key={l} value={l}>{l}</option>)}</select></label>
        </div>
        <div className="sb-pv-out">{previewName}</div>

        {/* Listes éditables */}
        <div className="sb-lists">
          <ChipList title={t('settings.list_categories')} addLabel={t('settings.chip_add')} items={cats} onCommit={commitCats} sanitize={sanitizeCategory} />
          <ChipList title={t('settings.list_personas')} addLabel={t('settings.chip_add')} items={personas} onCommit={commitPersonas} sanitize={sanitizePersona} />
          <ChipList title={t('settings.list_durations')} addLabel={t('settings.chip_add')} items={durs} onCommit={commitDurs} sanitize={sanitizeDuration} />
        </div>

        {/* Hors-nomenclature */}
        <div className="sb-offnom">
          <div className="sb-offnom-txt">
            <strong>{t('settings.offnom_title')}</strong>
            <span>{t('settings.offnom_desc')}</span>
          </div>
          <button className="sb-btn-outline" onClick={() => onNavigate && onNavigate('performance')}>{t('settings.open_performance')}</button>
        </div>
      </Block>

    </div>
  );
}

/* ── éditeur de chips (catégories / personas / durées) ── */
function ChipList({ title, items, onCommit, sanitize, addLabel }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = sanitize ? sanitize(draft) : draft.trim();
    if (!v || items.indexOf(v) !== -1) { setDraft(''); return; }
    onCommit([...items, v]);
    setDraft('');
  };
  return (
    <div className="sb-chiplist">
      <div className="sb-chiplist-title">{title}</div>
      <div className="sb-chiplist-body">
        {items.map(it => (
          <span key={it} className="sb-editchip">{it}<button onClick={() => onCommit(items.filter(x => x !== it))}>×</button></span>
        ))}
        <input
          className="sb-chip-add"
          value={draft}
          placeholder={addLabel || '+ add'}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          onBlur={add}
        />
      </div>
    </div>
  );
}
